import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import ClientPortalLayout from '@/components/client/ClientPortalLayout';
import DocumentUpload from '@/components/client/DocumentUpload';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { FileText, Trash2, Eye, Download } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';

interface Document {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  tax_year: number;
  extraction_status: string;
  uploaded_at: string;
  file_path: string;
  uploaded_by: string | null;
}

export default function ClientDocuments() {
  const { user } = useAuth();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDoc, setDeleteDoc] = useState<Document | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewDoc, setViewDoc] = useState<Document | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!user) return;

      try {
        // Get client record
        const { data: clientData } = await supabase
          .from('clients')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!clientData) {
          setLoading(false);
          return;
        }

        // Fetch documents
        const { data, error } = await supabase
          .from('client_documents')
          .select('id, file_name, file_type, file_size, tax_year, extraction_status, uploaded_at, file_path, uploaded_by')
          .eq('client_id', clientData.id)
          .order('uploaded_at', { ascending: false });

        if (error) throw error;
        setDocuments(data || []);
      } catch (error) {
        console.error('Error fetching documents:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();

    // Set up real-time subscription
    const channel = supabase
      .channel('client-documents')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'client_documents' }, () => {
        fetchDocuments();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      pending: "secondary",
      completed: "default",
      failed: "destructive",
      needs_review: "outline",
    };
    return <Badge variant={variants[status] || "secondary"}>{status}</Badge>;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const handleDelete = async (doc: Document) => {
    setIsDeleting(true);
    try {
      // 1. Delete from storage
      const { error: storageError } = await supabase.storage
        .from('tax-documents')
        .remove([doc.file_path]);
      
      if (storageError) throw storageError;

      // 2. Delete from database
      const { error: dbError } = await supabase
        .from('client_documents')
        .delete()
        .eq('id', doc.id);

      if (dbError) throw dbError;

      toast({
        title: 'Document deleted',
        description: 'Your document has been permanently removed.',
      });

      setDeleteDoc(null);
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: 'Deletion failed',
        description: 'There was an error deleting your document. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleViewDocument = async (doc: Document) => {
    setViewDoc(doc);
    setIsLoadingFile(true);
    setFileUrl(null);

    try {
      // Generate signed URL valid for 1 hour
      const { data, error } = await supabase.storage
        .from('tax-documents')
        .createSignedUrl(doc.file_path, 3600);

      if (error) throw error;

      if (data?.signedUrl) {
        setFileUrl(data.signedUrl);
      }
    } catch (error) {
      console.error('Error loading document:', error);
      toast({
        title: 'Failed to load document',
        description: 'There was an error loading the document preview.',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingFile(false);
    }
  };

  const handleDownload = async (doc: Document) => {
    try {
      const { data, error } = await supabase.storage
        .from('tax-documents')
        .download(doc.file_path);

      if (error) throw error;

      // Create download link
      const url = window.URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: 'Download started',
        description: 'Your document is being downloaded.',
      });
    } catch (error) {
      console.error('Error downloading document:', error);
      toast({
        title: 'Download failed',
        description: 'There was an error downloading the document.',
        variant: 'destructive',
      });
    }
  };

  return (
    <ClientPortalLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">My Documents</h1>
          <p className="text-muted-foreground mt-2">
            Upload and manage your tax documents securely
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Upload Section */}
          <div>
            <DocumentUpload />
          </div>

          {/* Uploaded Documents Section */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle>Uploaded Documents</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Loading documents...</p>
                  </div>
                ) : documents.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No documents yet</h3>
                    <p className="text-sm text-muted-foreground">
                      Upload your first document to get started
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {documents.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-start justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-start gap-3 flex-1">
                          <FileText className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{doc.file_name}</p>
                            <div className="flex flex-wrap gap-2 mt-1 text-xs text-muted-foreground">
                              <span>{doc.file_type}</span>
                              <span>•</span>
                              <span>{formatFileSize(doc.file_size)}</span>
                              <span>•</span>
                              <span>Tax Year {doc.tax_year}</span>
                              <span>•</span>
                              <span>{format(new Date(doc.uploaded_at), 'PP')}</span>
                            </div>
                            <div className="mt-2 flex gap-2">
                              {getStatusBadge(doc.extraction_status)}
                              {doc.uploaded_by && (
                                <Badge variant="outline" className="text-xs">
                                  Uploaded by Admin
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDocument(doc)}
                            className="text-primary hover:text-primary"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteDoc(doc)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Document Viewer Dialog */}
      <Dialog open={!!viewDoc} onOpenChange={() => {
        setViewDoc(null);
        setFileUrl(null);
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{viewDoc?.file_name}</DialogTitle>
            <DialogDescription>
              {viewDoc?.file_type.toUpperCase()} • Tax Year {viewDoc?.tax_year} • {viewDoc && formatFileSize(viewDoc.file_size)}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* File Preview */}
            {isLoadingFile ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : fileUrl ? (
              <div className="border rounded-lg overflow-hidden">
                {viewDoc?.file_name.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                  // Image preview
                  <img 
                    src={fileUrl} 
                    alt={viewDoc.file_name}
                    className="w-full h-auto max-h-[60vh] object-contain bg-muted"
                  />
                ) : viewDoc?.file_name.match(/\.pdf$/i) ? (
                  // PDF preview
                  <iframe
                    src={fileUrl}
                    className="w-full h-[60vh]"
                    title={viewDoc.file_name}
                  />
                ) : (
                  // Other file types - show info and download button
                  <div className="flex flex-col items-center justify-center py-12 space-y-4">
                    <FileText className="h-16 w-16 text-muted-foreground" />
                    <div className="text-center">
                      <p className="font-medium">Preview not available</p>
                      <p className="text-sm text-muted-foreground">
                        Click download to view this file
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                Failed to load document preview
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between items-center pt-4 border-t">
              <div className="flex gap-2">
                {getStatusBadge(viewDoc?.extraction_status || 'pending')}
                {viewDoc?.uploaded_by && (
                  <Badge variant="outline" className="text-xs">
                    Uploaded by Admin
                  </Badge>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => viewDoc && handleDownload(viewDoc)}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setViewDoc(null);
                    setFileUrl(null);
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteDoc} onOpenChange={() => setDeleteDoc(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteDoc?.file_name}"?
              This action cannot be undone and will permanently remove the file.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteDoc && handleDelete(deleteDoc)}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ClientPortalLayout>
  );
}
